import React, { useRef, useState, useEffect } from 'react';
import Icon from 'react-web-vector-icons';
import { createPortal } from 'react-dom';
import styles from './FilterIcon.module.scss';
import { valueMatchesFilter } from '../../utils/filterHelpers';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * CRM Data Column Filter Component
 * 
 * Domain Logic:
 * - Auto-detects column data types (string, number, date, boolean) from actual data
 * - Provides type-specific filtering options (date ranges, numeric comparisons, text patterns)
 * - Supports multi-select filtering with checkbox lists
 * - Maintains filter order and visual indicators for active filters
 * - Handles CRM data edge cases (null values, date formats, boolean fields)
 * 
 * @param {Object} props
 * @param {Object|string} props.column - Column definition or column ID
 * @param {Array<Object>} props.data - CRM data rows for type detection
 * @param {Function} props.onFilterChange - Filter change callback
 * @param {Array} [props.allUniqueValues] - All unique values for this column
 * @param {Object} [props.filters] - Current filter state
 * @param {string} [props.sortBy] - Currently sorted column
 * @param {string} [props.sortOrder] - Sort direction (asc/desc)
 * @param {number} [props.filterOrderIndex] - Visual filter order indicator
 */

// Helper Functions
/**
 * Parses date strings from CRM data formats
 * Handles various date formats used in CRM systems
 */
const parseDateString = (val) => {
  if (typeof val !== 'string') return null;
  const normalized = val.trim().replace(' ', 'T');
  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
};

const parseFilterDate = (val) => parseDateString(val);

/**
 * Auto-detects column data type from actual CRM data
 * Uses heuristics to determine if column contains dates, numbers, booleans, or strings
 */
const getColumnType = (column, data, columnId) => {
  if (typeof column === 'object' && column.type) return column.type;
  if (!data.length) return 'string';

  const values = data.map(row => row[columnId]).filter(v => !!v);
  if (!values.length) return 'string';

  const dateLikeCount = values.filter(val => parseDateString(val)).length;
  if (dateLikeCount / values.length >= 0.5) return 'date';
  if (typeof values[0] === 'boolean') return 'boolean';
  if (typeof values[0] === 'number') return 'number';
  return 'string';
};

const FilterIcon = ({ column, data, onFilterChange, allUniqueValues = [], filters = {}, sortBy, sortOrder, filterOrderIndex }) => {
  // State for filter dropdown and positioning
  /** @type {boolean} Whether filter dropdown is open */
  const [isOpen, setIsOpen] = useState(false);
  /** @type {Object} Dropdown position for proper placement */
  const [dropdownPosition, setDropdownPosition] = useState({ top: 150, left: 150 });
  /** @type {Array} Frozen options to prevent changes during filtering */
  const [frozenOptions, setFrozenOptions] = useState([]);
  /** @type {string} Detected column data type */
  const [columnType, setColumnType] = useState('string');

  const columnId = typeof column === 'string' ? column : column.id;
  const columnFilter = filters[columnId] || { textFilter: { type: '', value: '' }, checkboxFilters: [] };
  const { textFilter, checkboxFilters } = columnFilter;
  const sortDirection = sortBy === columnId ? sortOrder : '';

  const iconButtonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setColumnType(getColumnType(column, data, columnId));
  }, [data, column]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (!dropdownRef.current?.contains(event.target) && !iconButtonRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);
  const numberFilterOptions = ['Equals', 'Greater Than', 'Less Than',];
  const stringFilterOptions = ['Contains', 'Begins With', 'Ends With', 'Equals', 'Does Not Equal', 'Does Not Contain'];
  const dateFilterOptions = ['Equals', 'Before', 'After'];

  const checkboxFilterOptions =
  columnType === 'date'
    ? dateFilterOptions
    : columnType === 'number'
      ? numberFilterOptions
      : stringFilterOptions;

  const uniqueValues = [...allUniqueValues].sort((a, b) => {
    if (a === '(None)') return -1;
    if (b === '(None)') return 1;
    if (columnType === 'date') {
      const da = parseDateString(a);
      const db = parseDateString(b);
      if (da && db) return da - db;
      return da ? -1 : 1;
    }
    return String(a).localeCompare(String(b));
  });

  const filteredValues = (isOpen ? frozenOptions : uniqueValues).filter(val =>
    valueMatchesFilter(val, columnType, textFilter)
  );
 function NumberFilterInput({ textFilter, onChange, className }) {
  if (textFilter.type === 'Between') {
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className={className}
          type="number"
          placeholder="Min"
          value={textFilter.value?.min ?? ''}
          onChange={e =>
            onChange({
              ...textFilter,
              value: { ...textFilter.value, min: e.target.value }
            })
          }
          style={{ width: 80 }}
        />
        <input
          className={className}
          type="number"
          placeholder="Max"
          value={textFilter.value?.max ?? ''}
          onChange={e =>
            onChange({
              ...textFilter,
              value: { ...textFilter.value, max: e.target.value }
            })
          }
          style={{ width: 80 }}
        />
      </div>
    );
  }
  // Default (single input)
  return (
    <input
      className={className}
      type="number"
      placeholder={textFilter.type || 'Value'}
      value={typeof textFilter.value === 'object' ? '' : textFilter.value}
      onChange={e =>
        onChange({
          ...textFilter,
          value: e.target.value
        })
      }
      style={{ width: 120 }}
    />
  );
}

  /**
   * Updates column sorting while preserving existing filters
   */
  const handleSortChange = (direction) => {
    const current = filters[columnId] || { textFilter: { type: '', value: '' }, checkboxFilters: [] };
    onFilterChange(columnId, current.textFilter, current.checkboxFilters, direction);
  };
  
  /**
   * Toggles select all for filtered values in checkbox list
   */
  const handleSelectAll = () => {
    const allSelected = filteredValues.every(val => checkboxFilters.includes(val));
    const newFilters = allSelected
      ? checkboxFilters.filter(val => !filteredValues.includes(val))
      : [...new Set([...checkboxFilters, ...filteredValues])];
    onFilterChange(columnId, textFilter, newFilters, sortDirection);
  };

  /**
   * Toggles individual checkbox selection
   */
  const handleCheckboxChange = (value) => {
    const newFilters = checkboxFilters.includes(value)
      ? checkboxFilters.filter(v => v !== value)
      : [...checkboxFilters, value];
    onFilterChange(columnId, textFilter, newFilters, sortDirection);
  };

  /**
   * Opens filter dropdown with proper positioning
   * Freezes options to prevent changes during filtering
   */
  const handleIconClick = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      setFrozenOptions(allUniqueValues);
      const rect = iconButtonRef.current.getBoundingClientRect();
      const dropdownWidth = 300;
      let left = rect.left + window.scrollX;
      if (left + dropdownWidth > window.innerWidth - 8) left = Math.max(0, window.innerWidth - dropdownWidth - 8);
      setDropdownPosition({ top: rect.bottom + window.scrollY + 4, left });
    }
    setIsOpen(!isOpen);
  };

  /**
   * Clears all filters and sorting for this column
   */
  const clearFilters = () => {
    onFilterChange(columnId, { type: '', value: '' }, [], '');
  };

  return (
    <div className={styles.dropdownContainer}>
      <button
        ref={iconButtonRef}
        onClick={handleIconClick}
        style={{ background: 'none', border: 'none', marginLeft: 4, cursor: 'pointer' }}
      >
        <Icon 
          name="chevron-down" 
          font="Entypo" 
          size={16}
          style={{ color: (checkboxFilters.length || sortDirection) ? '#1976D2' : '#888' }}
        />
        <span style={{ marginLeft: 4, fontSize: 11, color: '#999' }}>
          {filterOrderIndex >= 0 ? `(${filterOrderIndex + 1})` : ''}
        </span>
      </button>
      {isOpen && createPortal(
        <div
          className={styles.dropdown}
          ref={dropdownRef}
          style={{ top: dropdownPosition.top, left: dropdownPosition.left, position: 'fixed' }}
        >
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Sort</div>
            <div className={styles.segmentedSortGroup}>
              {['asc', 'desc'].map(direction => (
                <button
                  key={direction}
                  className={`${styles.segmentedSortButton} ${sortDirection === direction ? styles.active : ''}`}
                  onClick={() => handleSortChange(sortDirection === direction ? '' : direction)}
                >
                  {direction === 'asc' ? 'A-Z' : 'Z-A'}
                </button>
              ))}
            </div>
          </div>

          <button className={styles.clearButton} onClick={clearFilters}>
            ↺ Clear Column Filter
          </button>

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Checkbox Filter</div>
            <select
              className={styles.select}
              value={textFilter.type || 'Contains'}
              onChange={e => onFilterChange(columnId, { ...textFilter, type: e.target.value }, checkboxFilters, sortDirection)}
            >
              {checkboxFilterOptions.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            {columnType === 'date' ? (
              <DatePicker
                selected={textFilter.value ? new Date(textFilter.value) : null}
                onChange={date =>
                  onFilterChange(
                    columnId,
                    { ...textFilter, value: date ? date.toISOString() : '' },
                    checkboxFilters,
                    sortDirection
                  )
                }
                className={styles.input}
                placeholderText="Pick Date"
                dateFormat="P"
                popperClassName="datePickerCalendar"
                disabledKeyboardNavigation
                monthsShown={1}
                isClearable
              />
            ) : (
              <input
                className={styles.input}
                type="text"
                value={textFilter.value}
                onChange={e =>
                  onFilterChange(
                    columnId,
                    { ...textFilter, value: e.target.value },
                    checkboxFilters,
                    sortDirection
                  )
                }
              />
            )}

            <div className={styles.checkboxContainer}>
              <label className={styles.selectAll}>
                <input
                  type="checkbox"
                  checked={filteredValues.length > 0 && filteredValues.every(val => checkboxFilters.includes(val))}
                  onChange={handleSelectAll}
                />
                Select All
              </label>
              {filteredValues.map(val => (
                <label key={val} className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={checkboxFilters.includes(val)}
                    onChange={() => handleCheckboxChange(val)}
                  />
                  {val}
                </label>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default FilterIcon;